import moment from 'moment';
import React from 'react';
import ReactDOM from 'react-dom';

import * as OnboardingActions from 'actions/OnboardingActions';
import OnboardingConstants from 'constants/OnboardingConstants';
import EntryDAO from 'dao/EntryDAO';
import FolderSelector from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as keyboard from 'util/Keyboard';
import {ElementFormEvent, ElementKeyboardEvent} from 'util/Types';

import CalendarActions from './CalendarActions';

interface Props {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

interface State {
  name: string;
  folderId: string;
}

class NewEntryPopoverContent extends React.Component<Props, State> {
  state: State = {
    name: `${this.props.startDate.toDate().toLocaleDateString()} Entry`,
    folderId: FolderDefaults.get(FolderDefaultsKey.NOTEBOOK)!,
  };

  nameInputRef = React.createRef<HTMLInputElement>();

  componentDidMount(): void {
    const nameNode = ReactDOM.findDOMNode(this.nameInputRef.current) as HTMLInputElement;
    nameNode.focus();
    nameNode.selectionStart = 0;
    nameNode.selectionEnd = nameNode.value.length;
  }

  createEntry = (): void => {
    EntryDAO.create({
      name: this.state.name,
      folder: this.state.folderId,
      initialDay: this.props.startDate,
      numDays: this.props.endDate.diff(this.props.startDate, 'days'),
    })
      .fail(notifications.notifyError)
      .done(() => {
        CalendarActions.closeCreatePopover();
        CalendarActions.fetchEntries(this.props.startDate, this.props.endDate);
        FolderDefaults.set(FolderDefaultsKey.NOTEBOOK, this.state.folderId);
        OnboardingActions.completeOnboardingTask(
          OnboardingConstants.MODULES.ENTRY,
          OnboardingConstants.CREATE_ENTRY
        );
      });
  };

  handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (keyboard.isEscapeKey(e)) {
      CalendarActions.closeCreatePopover();
    }
  };

  closePopover = (): void => {
    CalendarActions.closeCreatePopover();
  };

  canSubmit = (): boolean => this.state.name.length > 0 && this.state.folderId != null;

  handleSubmit = (e: ElementFormEvent): void => {
    e.preventDefault();
    if (!this.canSubmit()) {
      return;
    }
    this.createEntry();
  };

  render(): JSX.Element {
    return (
      <div className="newEntryPopover">
        <span className="close pull-right" onClick={this.closePopover}>
          {'\u00D7'}
        </span>
        <h4 className="u-tiny-spacing-top">New Entry</h4>
        <form onSubmit={this.handleSubmit}>
          <input
            type="text"
            placeholder="Entry Name"
            value={this.state.name}
            onChange={(e) => {
              this.setState({name: e.target.value});
            }}
            ref={this.nameInputRef}
            onKeyDown={this.handleKeyDown}
            // fixed-position popover listens to mouse down
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          />
          <div className="u-small-spacing-top">
            <FolderSelector
              enableRedesign={true}
              showNotebook={true}
              value={this.state.folderId}
              onChange={(e) => {
                this.setState({folderId: e.target.value});
              }}
              unsetOnFetchFail={true}
            />
          </div>
          <div className="u-small-spacing-top text-right">
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.SMALL}
              type="submit"
              disabled={!this.canSubmit()}
              text="Create"
            />
          </div>
        </form>
      </div>
    );
  }
}

export default NewEntryPopoverContent;
